import { useEffect, useState } from "react";

export default function Hero() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="services"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-white px-6 md:px-10 pt-20"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(79,70,229,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(79,70,229,0.035) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 80% 70% at 50% 40%, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 65%, rgba(255,255,255,1) 100%)",
        }}
      />

      {/* Glow blob */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: "10%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "800px",
          height: "400px",
          background: "radial-gradient(ellipse at center, rgba(79,70,229,0.07) 0%, transparent 70%)",
          filter: "blur(60px)",
        }}
      />

      {/* Content */}
      <div
        className={`relative z-10 text-center max-w-5xl mx-auto w-full transition-all duration-700 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-[#E5E7EB] rounded-full px-4 py-1.5 mb-10">
          <span className="w-1.5 h-1.5 rounded-full bg-[#4F46E5] inline-block animate-pulse"></span>
          <span className="text-xs font-medium text-[#6B7280] tracking-widest uppercase">
            Operational Intelligence Platform
          </span>
        </div>

        {/* Headline — Apple-scale */}
        <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-semibold text-[#1F2933] leading-[1.05] tracking-[-0.03em] mb-7">
          Precision Systems
          <br />
          <span
            className="text-transparent bg-clip-text"
            style={{ backgroundImage: "linear-gradient(135deg, #4F46E5 0%, #818CF8 100%)" }}
          >
            for Complex Work.
          </span>
        </h1>

        {/* Subheadline */}
        <p className="text-base md:text-xl text-[#6B7280] leading-relaxed max-w-2xl mx-auto mb-12 font-normal">
          Noveka AI designs and deploys intelligent systems that bring clarity, control, and
          seamless execution to construction, legal, and clinical workflows.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={() => scrollTo("contact")}
            className="px-8 py-3.5 bg-[#4F46E5] text-white text-sm font-medium rounded-[12px] hover:bg-[#4338CA] hover:shadow-[0_8px_24px_rgba(79,70,229,0.38)] hover:-translate-y-0.5 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Deploy Your System
          </button>
          <button
            onClick={() => scrollTo("industries")}
            className="px-8 py-3.5 bg-white text-[#1F2933] text-sm font-medium rounded-[12px] border border-[#E5E7EB] hover:border-[#C7C9CD] hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            View Capabilities <i className="ri-arrow-right-line text-sm"></i>
          </button>
        </div>

        {/* Floating system preview card */}
        <div
          className={`mt-20 mx-auto max-w-4xl transition-all duration-1000 delay-300 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
          }`}
        >
          <div className="relative rounded-[20px] border border-[#E5E7EB] bg-white overflow-hidden"
            style={{ boxShadow: "0 24px 80px rgba(0,0,0,0.10), 0 4px 16px rgba(0,0,0,0.05)" }}
          >
            {/* Window chrome */}
            <div className="flex items-center gap-2 px-5 py-4 border-b border-[#F3F4F6] bg-[#FAFAFA]">
              <span className="w-3 h-3 rounded-full bg-[#F87171]"></span>
              <span className="w-3 h-3 rounded-full bg-[#FBBF24]"></span>
              <span className="w-3 h-3 rounded-full bg-[#34D399]"></span>
              <span className="flex-1 mx-4 h-6 rounded-md bg-[#F3F4F6] flex items-center justify-center">
                <span className="text-[10px] text-[#9CA3AF] tracking-wide">noveka.ai / dashboard</span>
              </span>
            </div>

            {/* Dashboard body */}
            <div className="p-6 bg-[#FAFAFA]">
              {/* Top stats row */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                {[
                  { label: "Active Workflows", val: "2,841", up: "+12%" },
                  { label: "Tasks Completed", val: "18,492", up: "+28%" },
                  { label: "Response Time", val: "0.4s", up: "−64%" },
                  { label: "Uptime", val: "99.97%", up: "stable" },
                ].map((s) => (
                  <div key={s.label} className="bg-white rounded-[12px] border border-[#E5E7EB] px-4 py-3">
                    <p className="text-[10px] text-[#9CA3AF] mb-1 whitespace-nowrap">{s.label}</p>
                    <p className="text-lg font-semibold text-[#1F2933] tracking-[-0.02em] leading-none mb-1">{s.val}</p>
                    <span className="text-[10px] text-emerald-500 font-medium">{s.up}</span>
                  </div>
                ))}
              </div>

              {/* Main area */}
              <div className="grid grid-cols-5 gap-4">
                {/* Workflow list */}
                <div className="col-span-3 bg-white rounded-[12px] border border-[#E5E7EB] p-4">
                  <p className="text-xs font-semibold text-[#1F2933] mb-4">Live Workflows</p>
                  <div className="flex flex-col gap-2.5">
                    {[
                      { name: "Client Intake — Legal", status: "Running", color: "#4F46E5" },
                      { name: "Site Report — Construction", status: "Complete", color: "#10B981" },
                      { name: "Patient Follow-up — Clinical", status: "Queued", color: "#F59E0B" },
                      { name: "Permit Approval — Engineering", status: "Running", color: "#4F46E5" },
                      { name: "Invoice Automation", status: "Complete", color: "#10B981" },
                    ].map((w) => (
                      <div key={w.name} className="flex items-center justify-between py-1.5 border-b border-[#F9FAFB] last:border-0">
                        <div className="flex items-center gap-2.5">
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: w.color }}></span>
                          <span className="text-xs text-[#374151] whitespace-nowrap">{w.name}</span>
                        </div>
                        <span
                          className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                          style={{
                            color: w.color,
                            backgroundColor: w.color + "18",
                          }}
                        >
                          {w.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Activity panel */}
                <div className="col-span-2 flex flex-col gap-4">
                  <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-4 flex-1">
                    <p className="text-xs font-semibold text-[#1F2933] mb-3">Execution Rate</p>
                    <div className="flex items-end gap-1 h-16">
                      {[40, 55, 45, 70, 60, 80, 72, 90, 85, 95, 88, 100].map((h, i) => (
                        <div
                          key={i}
                          className="flex-1 rounded-sm"
                          style={{
                            height: `${h}%`,
                            backgroundColor: i >= 9 ? "#4F46E5" : "#EEF2FF",
                          }}
                        ></div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-[#4F46E5] rounded-[12px] p-4 text-white">
                    <p className="text-[10px] font-medium text-indigo-200 mb-1">Systems Active</p>
                    <p className="text-2xl font-semibold tracking-[-0.02em]">47</p>
                    <p className="text-[10px] text-indigo-200 mt-1">Across all industries</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="mt-6 text-xs text-[#C4C9D4] font-normal tracking-wide">
          Trusted by operators in construction, legal, and clinical environments
        </p>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent pointer-events-none" />
    </section>
  );
}
